import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserStatsDetail } from './user.service';

export interface OrderStatsDetail {
  count: number;
  products: number;
  amount: number;
}

export interface UserStatsPeriod {
  total: UserStatsDetail;
  day_1: UserStatsDetail;
  day_7: UserStatsDetail;
  day_30: UserStatsDetail;
  day_90: UserStatsDetail;
}

export interface OrderStatsPeriod {
  total: OrderStatsDetail;
  day_1: OrderStatsDetail;
  day_7: OrderStatsDetail;
  day_30: OrderStatsDetail;
  day_90: OrderStatsDetail;
}

export interface LanguageStats {
  total: number;
  confirmed: number;
  unconfirmed: number;
}

export interface LanguageStatsMap {
  [languageCode: string]: LanguageStats;
}

export interface PoolStatsDetail {
  amount: number;
}

export interface PoolStatsPeriod {
  total: PoolStatsDetail;
  day_1: PoolStatsDetail;
  day_7: PoolStatsDetail;
  day_30: PoolStatsDetail;
  day_90: PoolStatsDetail;
}

export interface PoolStatsMap {
  allpools?: PoolStatsPeriod;
  poolbank?: PoolStatsPeriod;
  poolcardprovider?: PoolStatsPeriod;
  pooldevelopment?: PoolStatsPeriod;
  pooloffice?: PoolStatsPeriod;
  poolchurch?: PoolStatsPeriod;
  poolmatze?: PoolStatsPeriod;
}

export interface ProductStatsDetail {
  quantity: number;
  revenue: number;
}

export interface ProductStatsPeriod {
  name: string;
  code: string;
  total: ProductStatsDetail;
  day_1: ProductStatsDetail;
  day_7: ProductStatsDetail;
  day_30: ProductStatsDetail;
  day_90: ProductStatsDetail;
}

export interface ProductStatsMap {
  [productId: string]: ProductStatsPeriod;
}

export interface FinancialAccountInfo {
  id: number;
  username: string;
  email: string;
  balance: number;
  turnover: number;
  /** Per-currency USDC balance/turnover held by this credit account (0 if none). */
  balance_usdc?: number;
  turnover_usdc?: number;
}

export interface FinancialUserInfo {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  balance: number;
  turnover: number;
}

export interface UsdtBscWalletInfo {
  address: string;
  balance: number | null;
}

export interface FinancialCreditStats {
  usdt_bsc_wallet?: UsdtBscWalletInfo;
  usdc_bsc_wallet?: UsdtBscWalletInfo;
  account_crypto: FinancialAccountInfo;
  account_fiat: FinancialAccountInfo;
  account_diffrent_payments: FinancialAccountInfo;
  /** USDC held across the credit accounts, summed 1:1 into total_balance. */
  usdc?: { balance: number; turnover: number };
  total_balance: number;
  total_turnover: number;
}

export interface CardAccountInfo {
  id: number;
  card_number: number;
  card_type: string;
  user_id: number;
  user_full_name?: string;
  user_email?: string;
  balance: number;
  turnover: number;
  initialpayment_done?: string;
}

export interface FinancialDebitStats {
  users: FinancialUserInfo[];
  total_balance: number;
  total_turnover: number;
  card_accounts?: CardAccountInfo[];
  card_accounts_total_balance?: number;
  card_accounts_total_turnover?: number;
}

export interface FinancialStats {
  credit: FinancialCreditStats;
  debit: FinancialDebitStats;
}

export interface AdminDashboardStats {
  users: UserStatsPeriod;
  orders: OrderStatsPeriod;
  languages: LanguageStatsMap;
  pools?: PoolStatsMap;
  products?: ProductStatsMap;
  financials?: FinancialStats;
}

export interface DailyOrderStat {
  date: string;
  hour_start?: string;
  hour_end?: string;
  week_start?: string;
  week_end?: string;
  orders_amount: number;
  topups_amount: number;
  payouts_amount: number;
  pooldevelopment_payouts_amount: number;
  total_amount: number;
}

export interface DailyOrderStatsResponse {
  group_by: 'hour' | 'day' | 'week';
  hours?: number;
  days?: number;
  weeks?: number;
  start_date: string;
  end_date: string;
  daily_stats: DailyOrderStat[];
}

@Injectable({ providedIn: 'root' })
export class AdminService {

  constructor(private http: HttpClient) {}

  getAdminDashboardStats(): Observable<AdminDashboardStats> {
    return this.http.get<AdminDashboardStats>('/admin/dashboard_stats');
  }

  getDailyOrderStats(options: { groupBy?: 'hour' | 'day' | 'week'; hours?: number; days?: number; weeks?: number } = {}): Observable<DailyOrderStatsResponse> {
    const params: Record<string, string> = {};

    if (options.groupBy === 'hour') {
      params['group_by'] = 'hour';
      params['hours'] = (options.hours || 48).toString();
    } else if (options.groupBy === 'week') {
      params['group_by'] = 'week';
      params['weeks'] = (options.weeks || 15).toString();
    } else {
      params['group_by'] = 'day';
      params['days'] = (options.days || 30).toString();
    }

    return this.http.get<DailyOrderStatsResponse>('/admin/daily_order_stats', { params });
  }
}
